// mock.ts

export type ServiceCategory =
  | 'Compounding Wholesale'
  | 'Compounding Direct'
  | 'Drug Substance Services';

export type CustomerTier = 'Strategic' | 'Growth' | 'Opportunistic';

export interface Customer {
  id: string;
  name: string;
  tier: CustomerTier;
}

export interface Product {
  id: string;
  name: string;
  uom: 'kg';
  currency: 'USD';
  primaryCategory: ServiceCategory;
}

export interface PricingRecord {
  id: string;
  customerId: string;
  productId: string;
  manufacturerId: string;

  serviceCategory: ServiceCategory;

  // Commercial / transactional
  quoteDate: string; // ISO date
  receiptDate: string; // ISO date (for GR / landed cost timing)
  qtyKg: number;

  // Upstream cost components (USD/kg)
  mfgUnitCost: number;
  freightImport: number;
  qaRegOverhead: number;
  volatilityReserve: number;

  // Downstream price (USD/kg)
  sellUnitPrice: number;

  status: 'Won' | 'Lost' | 'In Progress';
  expedite: boolean;
}

export const SERVICE_CATEGORIES: ServiceCategory[] = [
  'Compounding Wholesale',
  'Compounding Direct',
  'Drug Substance Services',
];

export const CUSTOMERS: Customer[] = [
  { id: 'C001', name: 'Aster Compounding Group', tier: 'Strategic' },
  { id: 'C002', name: 'BluePeak Telehealth', tier: 'Strategic' },
  { id: 'C003', name: 'Crescent Specialty Rx', tier: 'Strategic' },
  { id: 'C004', name: 'Dune Health Partners', tier: 'Growth' },
  { id: 'C005', name: 'Echelon Wellness Clinics', tier: 'Strategic' },
  { id: 'C006', name: 'Fidelity Compounding Network', tier: 'Growth' },
  { id: 'C007', name: 'GreenLine Pharmacy Services', tier: 'Strategic' },
  { id: 'C008', name: 'HarborPoint Care', tier: 'Growth' },
  { id: 'C009', name: 'Ionis Medical Supply', tier: 'Opportunistic' },
  { id: 'C010', name: 'Jasper Specialty Health', tier: 'Growth' },
  { id: 'C011', name: 'Keystone Clinics', tier: 'Strategic' },
  { id: 'C012', name: 'Lumen Telemed', tier: 'Strategic' },
  { id: 'C013', name: 'Meridian Rx Partners', tier: 'Growth' },
  { id: 'C014', name: 'NorthStar Compounding', tier: 'Strategic' },
  { id: 'C015', name: 'Orchid Health Collective', tier: 'Growth' },
  { id: 'C016', name: 'Pinnacle Pharmacy Group', tier: 'Strategic' },
  { id: 'C017', name: 'Quartz Clinical Solutions', tier: 'Opportunistic' },
  { id: 'C018', name: 'Redwood Care Network', tier: 'Growth' },
  { id: 'C019', name: 'Summit Specialty Clinics', tier: 'Strategic' },
  { id: 'C020', name: 'Trident Health Logistics', tier: 'Growth' },
];

/**
 * Changes requested:
 * - Remove Semaglutide and Tirzepatide
 * - Add Sermorelin Acetate, Glutathione, and Sirolimus (Rapamycin)
 *
 * Implementation approach:
 * - Re-purpose P001 and P002 to the new compounds (so existing pricing records continue to work)
 *   - P001: Sermorelin Acetate (Compounding Wholesale)
 *   - P002: Glutathione (Compounding Wholesale)
 * - Add new product P021: Sirolimus (Rapamycin) (Compounding Direct)
 */
export const PRODUCTS: Product[] = [
  {
    id: 'P001',
    name: 'Sermorelin Acetate',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Compounding Wholesale',
  },
  {
    id: 'P002',
    name: 'Glutathione',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Compounding Wholesale',
  },

  {
    id: 'P003',
    name: 'Liraglutide',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Compounding Wholesale',
  },
  {
    id: 'P004',
    name: 'Testosterone Cypionate (API)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Compounding Direct',
  },
  {
    id: 'P005',
    name: 'Progesterone (API)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Compounding Direct',
  },
  {
    id: 'P006',
    name: 'Estradiol (API)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Compounding Direct',
  },
  {
    id: 'P007',
    name: 'Minoxidil (API)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Compounding Direct',
  },
  {
    id: 'P008',
    name: 'Finasteride (API)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Compounding Direct',
  },
  {
    id: 'P009',
    name: 'Tadalafil (API)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Compounding Direct',
  },
  {
    id: 'P010',
    name: 'Sildenafil (API)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Compounding Direct',
  },
  {
    id: 'P011',
    name: 'NAD+ Precursor (API)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Compounding Wholesale',
  },
  {
    id: 'P012',
    name: 'Metformin (Drug Substance)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Drug Substance Services',
  },
  {
    id: 'P013',
    name: 'Atorvastatin (Drug Substance)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Drug Substance Services',
  },
  {
    id: 'P014',
    name: 'Losartan (Drug Substance)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Drug Substance Services',
  },
  {
    id: 'P015',
    name: 'Amlodipine (Drug Substance)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Drug Substance Services',
  },
  {
    id: 'P016',
    name: 'Omeprazole (Drug Substance)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Drug Substance Services',
  },
  {
    id: 'P017',
    name: 'Azithromycin (Drug Substance)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Drug Substance Services',
  },
  {
    id: 'P018',
    name: 'Doxycycline (Drug Substance)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Drug Substance Services',
  },
  {
    id: 'P019',
    name: 'Clindamycin Phosphate (API)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Compounding Wholesale',
  },
  {
    id: 'P020',
    name: 'Hydroquinone (API)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Compounding Direct',
  },

  // New product added
  {
    id: 'P021',
    name: 'Sirolimus (Rapamycin)',
    uom: 'kg',
    currency: 'USD',
    primaryCategory: 'Compounding Direct',
  },
];

// Pricing records (Semaglutide/Tirzepatide were removed by re-purposing P001/P002 to Sermorelin/Glutathione)
export const PRICING_RECORDS: PricingRecord[] = [
  // --- Sermorelin Acetate (P001) ---
  {
    id: 'R001',
    customerId: 'C002',
    productId: 'P001',
    manufacturerId: 'MFG-A',
    serviceCategory: 'Compounding Wholesale',
    quoteDate: '2025-09-12',
    receiptDate: '2025-09-05',
    qtyKg: 12,
    mfgUnitCost: 720,
    freightImport: 22,
    qaRegOverhead: 16,
    volatilityReserve: 18,
    sellUnitPrice: 960,
    status: 'Won',
    expedite: true,
  },

  // --- Glutathione (P002) ---
  {
    id: 'R002',
    customerId: 'C001',
    productId: 'P002',
    manufacturerId: 'MFG-B',
    serviceCategory: 'Compounding Wholesale',
    quoteDate: '2025-09-18',
    receiptDate: '2025-09-10',
    qtyKg: 10,
    mfgUnitCost: 860,
    freightImport: 25,
    qaRegOverhead: 18,
    volatilityReserve: 20,
    sellUnitPrice: 1180,
    status: 'Won',
    expedite: true,
  },

  // --- Liraglutide (P003) ---
  {
    id: 'R003',
    customerId: 'C003',
    productId: 'P003',
    manufacturerId: 'MFG-C',
    serviceCategory: 'Compounding Wholesale',
    quoteDate: '2025-10-02',
    receiptDate: '2025-09-25',
    qtyKg: 9,
    mfgUnitCost: 640,
    freightImport: 20,
    qaRegOverhead: 14,
    volatilityReserve: 16,
    sellUnitPrice: 880,
    status: 'Won',
    expedite: false,
  },

  // --- Compounding Direct APIs ---
  {
    id: 'R004',
    customerId: 'C005',
    productId: 'P004',
    manufacturerId: 'MFG-D',
    serviceCategory: 'Compounding Direct',
    quoteDate: '2025-10-08',
    receiptDate: '2025-10-01',
    qtyKg: 18,
    mfgUnitCost: 210,
    freightImport: 10,
    qaRegOverhead: 8,
    volatilityReserve: 6,
    sellUnitPrice: 340,
    status: 'Won',
    expedite: false,
  },
  {
    id: 'R005',
    customerId: 'C007',
    productId: 'P005',
    manufacturerId: 'MFG-D',
    serviceCategory: 'Compounding Direct',
    quoteDate: '2025-10-15',
    receiptDate: '2025-10-07',
    qtyKg: 22,
    mfgUnitCost: 145,
    freightImport: 8,
    qaRegOverhead: 7,
    volatilityReserve: 5,
    sellUnitPrice: 250,
    status: 'Won',
    expedite: false,
  },
  {
    id: 'R006',
    customerId: 'C011',
    productId: 'P006',
    manufacturerId: 'MFG-E',
    serviceCategory: 'Compounding Direct',
    quoteDate: '2025-10-21',
    receiptDate: '2025-10-14',
    qtyKg: 20,
    mfgUnitCost: 175,
    freightImport: 9,
    qaRegOverhead: 7,
    volatilityReserve: 6,
    sellUnitPrice: 290,
    status: 'Won',
    expedite: true,
  },
  {
    id: 'R007',
    customerId: 'C012',
    productId: 'P007',
    manufacturerId: 'MFG-F',
    serviceCategory: 'Compounding Direct',
    quoteDate: '2025-10-25',
    receiptDate: '2025-10-17',
    qtyKg: 30,
    mfgUnitCost: 95,
    freightImport: 7,
    qaRegOverhead: 6,
    volatilityReserve: 4,
    sellUnitPrice: 160,
    status: 'Won',
    expedite: false,
  },
  {
    id: 'R008',
    customerId: 'C014',
    productId: 'P008',
    manufacturerId: 'MFG-F',
    serviceCategory: 'Compounding Direct',
    quoteDate: '2025-11-01',
    receiptDate: '2025-10-24',
    qtyKg: 26,
    mfgUnitCost: 110,
    freightImport: 7,
    qaRegOverhead: 6,
    volatilityReserve: 5,
    sellUnitPrice: 190,
    status: 'Won',
    expedite: false,
  },
  {
    id: 'R009',
    customerId: 'C016',
    productId: 'P009',
    manufacturerId: 'MFG-G',
    serviceCategory: 'Compounding Direct',
    quoteDate: '2025-11-06',
    receiptDate: '2025-10-29',
    qtyKg: 28,
    mfgUnitCost: 130,
    freightImport: 8,
    qaRegOverhead: 7,
    volatilityReserve: 6,
    sellUnitPrice: 225,
    status: 'Won',
    expedite: true,
  },
  {
    id: 'R010',
    customerId: 'C019',
    productId: 'P010',
    manufacturerId: 'MFG-G',
    serviceCategory: 'Compounding Direct',
    quoteDate: '2025-11-10',
    receiptDate: '2025-11-02',
    qtyKg: 25,
    mfgUnitCost: 120,
    freightImport: 8,
    qaRegOverhead: 7,
    volatilityReserve: 6,
    sellUnitPrice: 210,
    status: 'Won',
    expedite: false,
  },

  // --- NAD+ precursor (P011) ---
  {
    id: 'R011',
    customerId: 'C002',
    productId: 'P011',
    manufacturerId: 'MFG-H',
    serviceCategory: 'Compounding Wholesale',
    quoteDate: '2025-11-14',
    receiptDate: '2025-11-06',
    qtyKg: 14,
    mfgUnitCost: 420,
    freightImport: 18,
    qaRegOverhead: 12,
    volatilityReserve: 12,
    sellUnitPrice: 620,
    status: 'Won',
    expedite: false,
  },

  // --- Drug Substance Services ---
  {
    id: 'R012',
    customerId: 'C003',
    productId: 'P012',
    manufacturerId: 'MFG-I',
    serviceCategory: 'Drug Substance Services',
    quoteDate: '2025-11-20',
    receiptDate: '2025-11-11',
    qtyKg: 40,
    mfgUnitCost: 22,
    freightImport: 2,
    qaRegOverhead: 3,
    volatilityReserve: 2,
    sellUnitPrice: 36,
    status: 'Won',
    expedite: false,
  },
  {
    id: 'R013',
    customerId: 'C011',
    productId: 'P013',
    manufacturerId: 'MFG-I',
    serviceCategory: 'Drug Substance Services',
    quoteDate: '2025-11-22',
    receiptDate: '2025-11-13',
    qtyKg: 32,
    mfgUnitCost: 48,
    freightImport: 3,
    qaRegOverhead: 4,
    volatilityReserve: 3,
    sellUnitPrice: 72,
    status: 'Won',
    expedite: false,
  },
  {
    id: 'R014',
    customerId: 'C015',
    productId: 'P014',
    manufacturerId: 'MFG-J',
    serviceCategory: 'Drug Substance Services',
    quoteDate: '2025-11-26',
    receiptDate: '2025-11-16',
    qtyKg: 35,
    mfgUnitCost: 26,
    freightImport: 2,
    qaRegOverhead: 3,
    volatilityReserve: 2,
    sellUnitPrice: 42,
    status: 'Won',
    expedite: false,
  },
  {
    id: 'R015',
    customerId: 'C018',
    productId: 'P015',
    manufacturerId: 'MFG-J',
    serviceCategory: 'Drug Substance Services',
    quoteDate: '2025-12-01',
    receiptDate: '2025-11-20',
    qtyKg: 33,
    mfgUnitCost: 34,
    freightImport: 2,
    qaRegOverhead: 3,
    volatilityReserve: 2,
    sellUnitPrice: 55,
    status: 'Won',
    expedite: true,
  },
  {
    id: 'R016',
    customerId: 'C020',
    productId: 'P016',
    manufacturerId: 'MFG-K',
    serviceCategory: 'Drug Substance Services',
    quoteDate: '2025-12-03',
    receiptDate: '2025-11-22',
    qtyKg: 29,
    mfgUnitCost: 38,
    freightImport: 2,
    qaRegOverhead: 3,
    volatilityReserve: 2,
    sellUnitPrice: 60,
    status: 'Won',
    expedite: false,
  },
  {
    id: 'R017',
    customerId: 'C007',
    productId: 'P017',
    manufacturerId: 'MFG-K',
    serviceCategory: 'Drug Substance Services',
    quoteDate: '2025-12-05',
    receiptDate: '2025-11-24',
    qtyKg: 24,
    mfgUnitCost: 75,
    freightImport: 3,
    qaRegOverhead: 4,
    volatilityReserve: 3,
    sellUnitPrice: 108,
    status: 'Won',
    expedite: false,
  },

  // --- Other wholesale/direct APIs ---
  {
    id: 'R018',
    customerId: 'C001',
    productId: 'P019',
    manufacturerId: 'MFG-L',
    serviceCategory: 'Compounding Wholesale',
    quoteDate: '2025-12-06',
    receiptDate: '2025-11-28',
    qtyKg: 16,
    mfgUnitCost: 180,
    freightImport: 10,
    qaRegOverhead: 10,
    volatilityReserve: 8,
    sellUnitPrice: 285,
    status: 'Won',
    expedite: true,
  },
  {
    id: 'R019',
    customerId: 'C014',
    productId: 'P020',
    manufacturerId: 'MFG-M',
    serviceCategory: 'Compounding Direct',
    quoteDate: '2025-12-08',
    receiptDate: '2025-11-30',
    qtyKg: 19,
    mfgUnitCost: 60,
    freightImport: 6,
    qaRegOverhead: 6,
    volatilityReserve: 4,
    sellUnitPrice: 120,
    status: 'Won',
    expedite: false,
  },

  // --- Sermorelin Acetate (P001) additional price points ---
  {
    id: 'R020',
    customerId: 'C016',
    productId: 'P001',
    manufacturerId: 'MFG-A',
    serviceCategory: 'Compounding Wholesale',
    quoteDate: '2025-12-10',
    receiptDate: '2025-12-02',
    qtyKg: 8,
    mfgUnitCost: 735,
    freightImport: 22,
    qaRegOverhead: 16,
    volatilityReserve: 18,
    sellUnitPrice: 985,
    status: 'Won',
    expedite: true,
  },
  {
    id: 'R021',
    customerId: 'C011',
    productId: 'P001',
    manufacturerId: 'MFG-A',
    serviceCategory: 'Compounding Wholesale',
    quoteDate: '2025-12-11',
    receiptDate: '2025-12-03',
    qtyKg: 7,
    mfgUnitCost: 705,
    freightImport: 22,
    qaRegOverhead: 16,
    volatilityReserve: 18,
    sellUnitPrice: 940,
    status: 'Won',
    expedite: false,
  },
  {
    id: 'R022',
    customerId: 'C012',
    productId: 'P001',
    manufacturerId: 'MFG-A',
    serviceCategory: 'Compounding Wholesale',
    quoteDate: '2025-12-12',
    receiptDate: '2025-12-04',
    qtyKg: 9,
    mfgUnitCost: 760,
    freightImport: 24,
    qaRegOverhead: 16,
    volatilityReserve: 20,
    sellUnitPrice: 1010,
    status: 'Won',
    expedite: true,
  },

  // --- Glutathione (P002) additional price points ---
  {
    id: 'R023',
    customerId: 'C003',
    productId: 'P002',
    manufacturerId: 'MFG-B',
    serviceCategory: 'Compounding Wholesale',
    quoteDate: '2025-12-13',
    receiptDate: '2025-12-06',
    qtyKg: 6,
    mfgUnitCost: 845,
    freightImport: 25,
    qaRegOverhead: 18,
    volatilityReserve: 20,
    sellUnitPrice: 1160,
    status: 'Won',
    expedite: false,
  },
  {
    id: 'R024',
    customerId: 'C005',
    productId: 'P002',
    manufacturerId: 'MFG-B',
    serviceCategory: 'Compounding Wholesale',
    quoteDate: '2025-12-14',
    receiptDate: '2025-12-06',
    qtyKg: 8,
    mfgUnitCost: 900,
    freightImport: 28,
    qaRegOverhead: 18,
    volatilityReserve: 22,
    sellUnitPrice: 1250,
    status: 'Won',
    expedite: true,
  },

  // --- Liraglutide (P003) additional price points ---
  {
    id: 'R025',
    customerId: 'C007',
    productId: 'P003',
    manufacturerId: 'MFG-C',
    serviceCategory: 'Compounding Wholesale',
    quoteDate: '2025-12-15',
    receiptDate: '2025-12-07',
    qtyKg: 7,
    mfgUnitCost: 620,
    freightImport: 20,
    qaRegOverhead: 14,
    volatilityReserve: 16,
    sellUnitPrice: 840,
    status: 'Won',
    expedite: false,
  },
  {
    id: 'R026',
    customerId: 'C008',
    productId: 'P003',
    manufacturerId: 'MFG-C',
    serviceCategory: 'Compounding Wholesale',
    quoteDate: '2025-12-16',
    receiptDate: '2025-12-07',
    qtyKg: 8,
    mfgUnitCost: 680,
    freightImport: 22,
    qaRegOverhead: 14,
    volatilityReserve: 18,
    sellUnitPrice: 920,
    status: 'Won',
    expedite: true,
  },

  // --- Minoxidil (P007) additional price points ---
  {
    id: 'R027',
    customerId: 'C014',
    productId: 'P007',
    manufacturerId: 'MFG-F',
    serviceCategory: 'Compounding Direct',
    quoteDate: '2025-12-17',
    receiptDate: '2025-12-10',
    qtyKg: 22,
    mfgUnitCost: 88,
    freightImport: 7,
    qaRegOverhead: 6,
    volatilityReserve: 4,
    sellUnitPrice: 150,
    status: 'Won',
    expedite: false,
  },
  {
    id: 'R028',
    customerId: 'C016',
    productId: 'P007',
    manufacturerId: 'MFG-F',
    serviceCategory: 'Compounding Direct',
    quoteDate: '2025-12-18',
    receiptDate: '2025-12-10',
    qtyKg: 18,
    mfgUnitCost: 102,
    freightImport: 7,
    qaRegOverhead: 6,
    volatilityReserve: 5,
    sellUnitPrice: 175,
    status: 'Won',
    expedite: true,
  },

  // --- Tadalafil (P009) additional price points ---
  {
    id: 'R029',
    customerId: 'C012',
    productId: 'P009',
    manufacturerId: 'MFG-G',
    serviceCategory: 'Compounding Direct',
    quoteDate: '2025-12-19',
    receiptDate: '2025-12-12',
    qtyKg: 20,
    mfgUnitCost: 140,
    freightImport: 8,
    qaRegOverhead: 7,
    volatilityReserve: 6,
    sellUnitPrice: 260,
    status: 'Won',
    expedite: false,
  },
  {
    id: 'R030',
    customerId: 'C019',
    productId: 'P009',
    manufacturerId: 'MFG-G',
    serviceCategory: 'Compounding Direct',
    quoteDate: '2025-12-20',
    receiptDate: '2025-12-12',
    qtyKg: 15,
    mfgUnitCost: 175,
    freightImport: 10,
    qaRegOverhead: 7,
    volatilityReserve: 8,
    sellUnitPrice: 315,
    status: 'Won',
    expedite: true,
  },

  // --- Atorvastatin (P013) additional price points ---
  {
    id: 'R031',
    customerId: 'C015',
    productId: 'P013',
    manufacturerId: 'MFG-I',
    serviceCategory: 'Drug Substance Services',
    quoteDate: '2025-12-21',
    receiptDate: '2025-12-14',
    qtyKg: 28,
    mfgUnitCost: 52,
    freightImport: 3,
    qaRegOverhead: 4,
    volatilityReserve: 3,
    sellUnitPrice: 78,
    status: 'Won',
    expedite: false,
  },

  // --- Doxycycline (P018) additional price points ---
  {
    id: 'R032',
    customerId: 'C018',
    productId: 'P018',
    manufacturerId: 'MFG-K',
    serviceCategory: 'Drug Substance Services',
    quoteDate: '2025-12-22',
    receiptDate: '2025-12-15',
    qtyKg: 26,
    mfgUnitCost: 92,
    freightImport: 3,
    qaRegOverhead: 4,
    volatilityReserve: 3,
    sellUnitPrice: 125,
    status: 'Won',
    expedite: false,
  },
  {
    id: 'R033',
    customerId: 'C020',
    productId: 'P018',
    manufacturerId: 'MFG-K',
    serviceCategory: 'Drug Substance Services',
    quoteDate: '2025-12-23',
    receiptDate: '2025-12-16',
    qtyKg: 22,
    mfgUnitCost: 100,
    freightImport: 3,
    qaRegOverhead: 4,
    volatilityReserve: 4,
    sellUnitPrice: 140,
    status: 'Won',
    expedite: true,
  },

  // --- NAD+ precursor (P011) additional price points ---
  {
    id: 'R034',
    customerId: 'C001',
    productId: 'P011',
    manufacturerId: 'MFG-H',
    serviceCategory: 'Compounding Wholesale',
    quoteDate: '2025-12-24',
    receiptDate: '2025-12-17',
    qtyKg: 11,
    mfgUnitCost: 390,
    freightImport: 18,
    qaRegOverhead: 12,
    volatilityReserve: 12,
    sellUnitPrice: 610,
    status: 'Won',
    expedite: false,
  },

  // --- Sirolimus (Rapamycin) (P021) new price points ---
  {
    id: 'R035',
    customerId: 'C016',
    productId: 'P021',
    manufacturerId: 'MFG-N',
    serviceCategory: 'Compounding Direct',
    quoteDate: '2025-12-26',
    receiptDate: '2025-12-18',
    qtyKg: 6,
    mfgUnitCost: 520,
    freightImport: 16,
    qaRegOverhead: 12,
    volatilityReserve: 14,
    sellUnitPrice: 760,
    status: 'Won',
    expedite: true,
  },
  {
    id: 'R036',
    customerId: 'C019',
    productId: 'P021',
    manufacturerId: 'MFG-N',
    serviceCategory: 'Compounding Direct',
    quoteDate: '2025-12-27',
    receiptDate: '2025-12-19',
    qtyKg: 5,
    mfgUnitCost: 480,
    freightImport: 15,
    qaRegOverhead: 12,
    volatilityReserve: 12,
    sellUnitPrice: 710,
    status: 'Won',
    expedite: false,
  },
  {
    id: 'R037',
    customerId: 'C012',
    productId: 'P021',
    manufacturerId: 'MFG-O',
    serviceCategory: 'Compounding Direct',
    quoteDate: '2025-12-28',
    receiptDate: '2025-12-20',
    qtyKg: 7,
    mfgUnitCost: 545,
    freightImport: 18,
    qaRegOverhead: 12,
    volatilityReserve: 15,
    sellUnitPrice: 795,
    status: 'Won',
    expedite: true,
  },
];

export function landedCostPerKg(r: PricingRecord): number {
  return (
    r.mfgUnitCost + r.freightImport + r.qaRegOverhead + r.volatilityReserve
  );
}

export function grossMarginPerKg(r: PricingRecord): number {
  return r.sellUnitPrice - landedCostPerKg(r);
}

export function grossMarginPct(r: PricingRecord): number {
  const denom = r.sellUnitPrice === 0 ? 1 : r.sellUnitPrice;
  return (grossMarginPerKg(r) / denom) * 100;
}

export function profitDollars(r: PricingRecord): number {
  return grossMarginPerKg(r) * r.qtyKg;
}

export function filterRecordsByCategory(
  records: PricingRecord[],
  cat: ServiceCategory | 'All'
): PricingRecord[] {
  return cat === 'All'
    ? records
    : records.filter(r => r.serviceCategory === cat);
}

export function topCustomersByProfit(
  records: PricingRecord[],
  customers: Customer[],
  n: number
): Array<{ customer: Customer; profit: number }> {
  const byId = new Map<string, number>();
  for (let i = 0; i < records.length; i += 1) {
    const r = records[i];
    byId.set(r.customerId, (byId.get(r.customerId) ?? 0) + profitDollars(r));
  }
  return customers
    .map(c => ({ customer: c, profit: byId.get(c.id) ?? 0 }))
    .sort((a, b) => b.profit - a.profit)
    .slice(0, n);
}

export function topProductsByProfit(
  records: PricingRecord[],
  products: Product[],
  n: number
): Array<{ product: Product; profit: number }> {
  const byId = new Map<string, number>();
  for (let i = 0; i < records.length; i += 1) {
    const r = records[i];
    byId.set(r.productId, (byId.get(r.productId) ?? 0) + profitDollars(r));
  }
  return products
    .map(p => ({ product: p, profit: byId.get(p.id) ?? 0 }))
    .sort((a, b) => b.profit - a.profit)
    .slice(0, n);
}
